import type { EstipulanteLegacyKeyParsed, VinculoEstipulanteItem } from '../types/estipulanteVinculoType';
import type { VinculoNegocio } from '../types/apiType';

/** Single-digit line-of-business code → display label (fluxologin: 3=Saúde, 4=Vida, 5=Previdência). */
export const ESTIPULANTE_LINE_OF_BUSINESS_LABELS: Record<string, string> = {
  '3': 'Saúde',
  '4': 'Vida',
  '5': 'Previdência',
};

export type EstipulanteLineOfBusinessDigit = '3' | '4' | '5';
export type BackofficeSearchProduct = 'vida' | 'previdencia';

export function lineOfBusinessFromDigit(
  digit: EstipulanteLineOfBusinessDigit,
): { ramoCodigo: string; ramoNome: string } {
  return {
    ramoCodigo: digit,
    ramoNome: ESTIPULANTE_LINE_OF_BUSINESS_LABELS[digit],
  };
}

export function backofficeSearchProductToRamo(
  product: BackofficeSearchProduct,
): { ramoCodigo: string; ramoNome: string } {
  return product === 'vida' ? lineOfBusinessFromDigit('4') : lineOfBusinessFromDigit('5');
}

const SYSTEM_PREFIX_LENGTH = 4;
const CPF_DIGITS_LENGTH = 11;
const CNPJ_ALNUM_LENGTH = 14;
const POLICY_NUMBER_MAX_LENGTH = 11;

const DEFAULT_LEGACY_SYSTEM = 'ESTP';

function normalizeDocumentoSegment(raw: string): { documento: string; tipoDocumento: 'cpf' | 'cnpj' } | null {
  const alnum = raw.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
  if (alnum.length === CPF_DIGITS_LENGTH && /^\d{11}$/.test(alnum)) {
    return { documento: alnum, tipoDocumento: 'cpf' };
  }
  if (alnum.length === CNPJ_ALNUM_LENGTH) {
    return { documento: alnum, tipoDocumento: 'cnpj' };
  }
  return null;
}

/** Resolve document fields for `policySelection` / store (CPF 11 or CNPJ 14). */
export function resolveEstipulanteDocumentoFields(raw: string | null | undefined): {
  documento: string;
  tipoDocumento: 'cpf' | 'cnpj';
  cnpj: string;
} {
  const normalized = normalizeDocumentoSegment(String(raw ?? ''));
  if (normalized) {
    return { ...normalized, cnpj: normalized.documento };
  }
  const fallback = String(raw ?? '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
  return {
    documento: fallback,
    tipoDocumento: fallback.length === CPF_DIGITS_LENGTH ? 'cpf' : 'cnpj',
    cnpj: fallback,
  };
}

/**
 * Builds ESTP legacy key: PREFIX.DOCUMENTO(CPF 11 or CNPJ 14).policy(1–11).LOB(1 digit 3|4|5).
 */
export function buildEstipulanteLegacyKey(
  documento: string,
  numApolice: number | string,
  ramoDigit: '3' | '4' | '5'
): string {
  const normalized = normalizeDocumentoSegment(documento);
  if (!normalized) {
    throw new Error('Documento must be CPF (11 digits) or CNPJ (14 alphanumeric) for legacy key');
  }
  const policyDigits = String(numApolice).replace(/\D/g, '');
  if (policyDigits.length < 1 || policyDigits.length > POLICY_NUMBER_MAX_LENGTH) {
    throw new Error('Invalid policy number for legacy key');
  }
  const policySegment = policyDigits.padStart(POLICY_NUMBER_MAX_LENGTH, '0');
  return `${DEFAULT_LEGACY_SYSTEM}.${normalized.documento}.${policySegment}.${ramoDigit}`;
}

/**
 * Parses policyholder legacy key: PREFIX(4).DOCUMENTO(CPF 11 | CNPJ 14).POLICY_NO(1–11).LOB(1 digit: 3|4|5).
 */
export function parseEstipulanteLegacyKey(legacyKey: string): EstipulanteLegacyKeyParsed | null {
  if (!legacyKey || typeof legacyKey !== 'string') {
    return null;
  }

  const parts = legacyKey.trim().split('.');
  if (parts.length !== 4) {
    return null;
  }

  const [systemPrefix, documentoRaw, policyRaw, lobRaw] = parts;

  if (systemPrefix.length !== SYSTEM_PREFIX_LENGTH || !/^[A-Za-z0-9]{4}$/.test(systemPrefix)) {
    return null;
  }

  const normalized = normalizeDocumentoSegment(documentoRaw);
  if (!normalized) {
    return null;
  }

  const policyNumber = policyRaw.replace(/\D/g, '');
  if (policyNumber.length < 1 || policyNumber.length > POLICY_NUMBER_MAX_LENGTH) {
    return null;
  }

  if (!/^[345]$/.test(lobRaw)) {
    return null;
  }

  const lineOfBusinessName = ESTIPULANTE_LINE_OF_BUSINESS_LABELS[lobRaw];
  if (!lineOfBusinessName) {
    return null;
  }

  const { documento, tipoDocumento } = normalized;

  return {
    sistema: systemPrefix,
    documento,
    tipoDocumento,
    /** @deprecated alias of `documento` for store/UI compatibility */
    cnpj: documento,
    numeroApolice: policyNumber,
    ramoCodigo: lobRaw,
    ramoNome: lineOfBusinessName,
  };
}

/** Keeps only business links whose legacy key matches the expected format. */
export function mapValidEstipulanteBusinessLinks(vinculos: VinculoNegocio[]): VinculoEstipulanteItem[] {
  const out: VinculoEstipulanteItem[] = [];
  for (const v of vinculos) {
    const key = v.representacao?.chaveLegado?.trim() ?? '';
    const parsed = parseEstipulanteLegacyKey(key);
    if (parsed) {
      out.push({ vinculo: v, parsed });
    }
  }
  return out;
}
